import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Popper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import CategoryIcon from '@material-ui/icons/Category';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Page from 'src/components/Page';
import AlertScreen from 'src/components/Alert';
import axios from 'src/utils/axios';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  spacer: {
    flexGrow: 1
  },
  ustRow: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    marginTop: theme.spacing(0.5)
  },
  grupRow: {
    paddingLeft: theme.spacing(5)
  },
  ekipmanRow: {
    paddingLeft: theme.spacing(9)
  },
  passive: {
    opacity: 0.5
  },
  stokChip: {
    marginLeft: theme.spacing(1)
  },
  uzmanlikGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    padding: '2px 8px 4px',
    marginTop: 4,
    borderRadius: 12,
    backgroundColor: theme.palette.grey[100]
  },
  uzmanlikLabel: {
    fontWeight: 600,
    fontSize: 12,
    marginRight: 2,
    color: theme.palette.text.secondary
  },
  uzmanlikChip: {
    marginLeft: 2
  },
  detayWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  fieldPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    marginTop: 4,
    marginRight: 6,
    borderRadius: 12,
    backgroundColor: theme.palette.grey[100],
    fontSize: 12,
    maxWidth: '100%'
  },
  fieldLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  fieldValue: {
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 280
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center'
  },
  emptyChild: {
    paddingLeft: theme.spacing(9),
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  fotoWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  fotoThumb: {
    position: 'relative',
    width: 84,
    height: 84,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100]
  },
  fotoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  fotoDel: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 4,
    zIndex: 2,
    backgroundColor: 'rgba(0,0,0,0.55)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.75)'
    }
  },
  fotoBox: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  }
}));

const emptyForm = {
  Id: 0,
  UstGrupId: '',
  GrupId: '',
  Aciklama: '',
  Description: '',
  StokNo: '',
  Marka: '',
  Model: '',
  Uzmanliklar: [], // seçili uzmanlık option nesneleri
  KalibrasyonIhtiyaci: false,
  KalibrasyonGun: '', // gün sayısı
  Active: true
};

// Autocomplete listesini her zaman aşağı doğru aç (yukarı flip etme)
const AsagiPopper = props => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{ flip: { enabled: false } }}
  />
);

// Dosyayı data-URL (base64) olarak oku
const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const IZIN_VERILEN_TIPLER = ['image/jpeg', 'image/png'];
const MAX_FOTO_BYTE = 10 * 1024 * 1024; // 10 MB

// Ağaçta ekipmanın fotoğrafını gösteren küçük thumbnail (tembel yükleme:
// yalnızca grup açıldığında / satır render olduğunda çekilir)
function FotoThumb({ ekipmanId, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    axios
      .get(`/GetEkipmanDosyalar/${ekipmanId}`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (!list.length) return null;
        return axios.get(`/GetEkipmanDosya/${list[0].Id}`).then(r2 => {
          const d = r2.data || {};
          if (alive && d.FileData) {
            setUrl(
              `data:${d.ContentType ||
                list[0].ContentType ||
                'image/jpeg'};base64,${d.FileData}`
            );
          }
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ekipmanId]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      title="Büyütmek için tıklayın"
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick(url);
      }}
      style={{
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 4,
        marginRight: 8,
        verticalAlign: 'middle',
        cursor: 'pointer'
      }}
    />
  );
}

function EkipmanAdminYonetimi() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [ustGruplar, setUstGruplar] = useState([]);
  const [gruplar, setGruplar] = useState([]);
  const [ekipmanlar, setEkipmanlar] = useState([]);
  const [uzmanliklar, setUzmanliklar] = useState([]); // ana liste
  const [ekipmanUzmanliklar, setEkipmanUzmanliklar] = useState([]); // bağlantılar
  const [mevcutFotolar, setMevcutFotolar] = useState([]); // düzenlemede yüklü fotoğraflar
  const [yeniFotolar, setYeniFotolar] = useState([]); // kayıtla birlikte yüklenecekler
  const [bigFoto, setBigFoto] = useState({ open: false, url: null }); // ağaçta foto büyütme

  const [showPassive, setShowPassive] = useState(false);
  const [expandedUst, setExpandedUst] = useState({});
  const [expandedGrup, setExpandedGrup] = useState({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLevel, setDialogLevel] = useState('ust'); // 'ust' | 'grup' | 'ekipman'
  const [form, setForm] = useState(emptyForm);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackType, setSnackType] = useState('success');
  const [snackMessage, setSnackMessage] = useState('');

  // Onay dialog'u (MUI - edit dialog'un üstünde güvenli şekilde açılır)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const askConfirm = (title, message, onConfirm) =>
    setConfirmState({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmState(s => ({ ...s, open: false }));

  useEffect(() => {
    fetchAll();
  }, []);

  const notify = (type, message) => {
    setSnackType(type);
    setSnackMessage(message);
    setSnackOpen(true);
  };

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get('/GetEkipmanUstGruplar'),
      axios.get('/GetEkipmanGruplar'),
      axios.get('/GetEkipmanlar'),
      axios.get('/GetUzmanliklar'),
      axios.get('/GetEkipmanUzmanliklar')
    ])
      .then(([ustRes, grupRes, ekipRes, uzmRes, ekipUzmRes]) => {
        setUstGruplar(Array.isArray(ustRes.data) ? ustRes.data : []);
        setGruplar(Array.isArray(grupRes.data) ? grupRes.data : []);
        setEkipmanlar(Array.isArray(ekipRes.data) ? ekipRes.data : []);
        setUzmanliklar(Array.isArray(uzmRes.data) ? uzmRes.data : []);
        setEkipmanUzmanliklar(
          Array.isArray(ekipUzmRes.data) ? ekipUzmRes.data : []
        );
      })
      .catch(() => {
        notify('error', 'Veriler yüklenirken bir hata oluştu.');
      })
      .finally(() => setLoading(false));
  };

  const visible = item => showPassive || item.Active;

  // Bir ekipmana bağlı uzmanlık bağlantıları
  const uzmanlikForEkipman = ekipmanId =>
    ekipmanUzmanliklar.filter(x => x.EkipmanId === ekipmanId);

  // Düzenlemede ekipmanın mevcut fotoğraflarını yükle
  const loadFotolar = ekipmanId => {
    axios
      .get(`/GetEkipmanDosyalar/${ekipmanId}`)
      .then(async res => {
        const list = Array.isArray(res.data) ? res.data : [];
        const withData = await Promise.all(
          list.map(async m => {
            try {
              const r = await axios.get(`/GetEkipmanDosya/${m.Id}`);
              const d = r.data || {};
              const ct = d.ContentType || m.ContentType || 'image/jpeg';
              return {
                Id: m.Id,
                DosyaAdi: m.DosyaAdi,
                dataUrl: d.FileData ? `data:${ct};base64,${d.FileData}` : null
              };
            } catch (err) {
              return { Id: m.Id, DosyaAdi: m.DosyaAdi, dataUrl: null };
            }
          })
        );
        setMevcutFotolar(withData);
      })
      .catch(() => setMevcutFotolar([]));
  };

  // Foto seçimi (tek foto; kayıtla birlikte yüklenir, mevcut foto değişir)
  const handleFotoSec = async e => {
    const file = (e.target.files || [])[0];
    e.target.value = ''; // aynı dosyayı tekrar seçebilmek için sıfırla
    if (!file) return;
    if (!IZIN_VERILEN_TIPLER.includes(file.type)) {
      notify('error', 'Sadece JPG/PNG yüklenebilir.');
      return;
    }
    if (file.size > MAX_FOTO_BYTE) {
      notify('error', "Dosya 10MB'tan büyük.");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setYeniFotolar([
      {
        DosyaAdi: file.name,
        ContentType: file.type,
        FileData: String(dataUrl).split(',')[1],
        dataUrl
      }
    ]);
  };

  const removeYeniFoto = () => {
    setYeniFotolar([]);
    notify(
      'info',
      mevcutFotolar.length > 0
        ? 'Seçilen yeni fotoğraf kaldırıldı; mevcut fotoğraf korunacak.'
        : 'Seçilen fotoğraf kaldırıldı.'
    );
  };

  const handleDeleteFoto = id => {
    askConfirm(
      'Fotoğrafı Sil',
      'Bu fotoğrafı silmek istediğinize emin misiniz?',
      () => {
        setLoading(true);
        axios
          .get(`/DeleteEkipmanDosya/${id}`)
          .then(res => {
            if (res.data && res.data.ErrorCode === '0') {
              setMevcutFotolar(p => p.filter(x => x.Id !== id));
              notify('success', 'Fotoğraf silindi.');
            } else {
              notify(
                'error',
                (res.data && res.data.ErrorDescription) ||
                  'Fotoğraf silinemedi.'
              );
            }
          })
          .catch(() => notify('error', 'Silme sırasında hata oluştu.'))
          .finally(() => setLoading(false));
      }
    );
  };

  const toggleUst = id => setExpandedUst(p => ({ ...p, [id]: !p[id] }));
  const toggleGrup = id => setExpandedGrup(p => ({ ...p, [id]: !p[id] }));

  // ---- Dialog açıcılar ----
  const openAddUst = () => {
    setDialogLevel('ust');
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };
  const openEditUst = row => {
    setDialogLevel('ust');
    setForm({
      ...emptyForm,
      Id: row.Id,
      Aciklama: row.Aciklama,
      Active: row.Active
    });
    setDialogOpen(true);
  };
  const openAddGrup = ustGrupId => {
    setDialogLevel('grup');
    setForm({ ...emptyForm, UstGrupId: ustGrupId });
    setDialogOpen(true);
  };
  const openEditGrup = row => {
    setDialogLevel('grup');
    setForm({
      ...emptyForm,
      Id: row.Id,
      UstGrupId: row.UstGrupId,
      Aciklama: row.Aciklama,
      Active: row.Active
    });
    setDialogOpen(true);
  };
  const openAddEkipman = (ustGrupId, grupId) => {
    setDialogLevel('ekipman');
    setForm({ ...emptyForm, UstGrupId: ustGrupId, GrupId: grupId });
    setMevcutFotolar([]);
    setYeniFotolar([]);
    setDialogOpen(true);
  };
  const openEditEkipman = row => {
    setDialogLevel('ekipman');
    const seciliIdler = uzmanlikForEkipman(row.Id).map(x => x.UzmanlikId);
    const seciliUzmanliklar = uzmanliklar.filter(u =>
      seciliIdler.includes(u.Kod)
    );
    setForm({
      ...emptyForm,
      Id: row.Id,
      UstGrupId: row.UstGrupId,
      GrupId: row.GrupId,
      Description: row.Description,
      StokNo: row.StokNo || '',
      Aciklama: row.Aciklama || '',
      Marka: row.Marka || '',
      Model: row.Model || '',
      Uzmanliklar: seciliUzmanliklar,
      KalibrasyonIhtiyaci: !!row.KalibrasyonIhtiyaci,
      KalibrasyonGun:
        row.KalibrasyonGun != null ? String(row.KalibrasyonGun) : '',
      Active: row.Active
    });
    setMevcutFotolar([]);
    setYeniFotolar([]);
    loadFotolar(row.Id);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const setFormField = (field, value) =>
    setForm(p => ({ ...p, [field]: value }));

  // Dialog'da üst grup değişince grup seçimini sıfırla
  const handleUstChangeInDialog = value => {
    setForm(p => ({ ...p, UstGrupId: value, GrupId: '' }));
  };

  const dialogTitle = () => {
    const ad = { ust: 'Üst Grup', grup: 'Grup', ekipman: 'Ekipman' }[
      dialogLevel
    ];
    return `${form.Id > 0 ? 'Düzenle' : 'Yeni'} - ${ad}`;
  };

  const handleSave = () => {
    let url;
    let payload;

    if (dialogLevel === 'ust') {
      if (!form.Aciklama.trim()) return notify('error', 'Açıklama boş olamaz.');
      url = '/SetEkipmanUstGrup';
      payload = {
        Id: form.Id,
        Aciklama: form.Aciklama.trim(),
        Active: form.Active
      };
    } else if (dialogLevel === 'grup') {
      if (!form.UstGrupId) return notify('error', 'Üst grup seçilmelidir.');
      if (!form.Aciklama.trim()) return notify('error', 'Açıklama boş olamaz.');
      url = '/SetEkipmanGrup';
      payload = {
        Id: form.Id,
        UstGrupId: Number(form.UstGrupId),
        Aciklama: form.Aciklama.trim(),
        Active: form.Active
      };
    } else {
      if (!form.UstGrupId) return notify('error', 'Üst grup seçilmelidir.');
      if (!form.GrupId) return notify('error', 'Grup seçilmelidir.');
      if (!form.Description.trim())
        return notify('error', 'Donanım adı boş olamaz.');

      url = '/SetEkipman';
      payload = {
        Id: form.Id,
        UstGrupId: Number(form.UstGrupId),
        GrupId: Number(form.GrupId),
        Description: form.Description.trim(),
        StokNo: form.StokNo ? form.StokNo.trim() : '',
        Aciklama: form.Aciklama ? form.Aciklama.trim() : '',
        Marka: form.Marka ? form.Marka.trim() : '',
        Model: form.Model ? form.Model.trim() : '',
        KalibrasyonIhtiyaci: form.KalibrasyonIhtiyaci,
        KalibrasyonGun:
          form.KalibrasyonIhtiyaci && form.KalibrasyonGun !== ''
            ? Number(form.KalibrasyonGun)
            : null,
        Active: form.Active,
        UzmanlikIds: (form.Uzmanliklar || []).map(u => u.Kod)
      };
    }

    setLoading(true);
    axios
      .post(url, payload)
      .then(async res => {
        if (res.data && res.data.ErrorCode === '0') {
          let fotoHata = false;
          // Ekipman kaydı sonrası yeni fotoğrafları yükle (kayıtla birlikte)
          if (dialogLevel === 'ekipman' && yeniFotolar.length > 0) {
            const ekipmanId = res.data.Id || form.Id;
            try {
              await Promise.all(
                yeniFotolar.map(f =>
                  axios.post('/UploadEkipmanDosya', {
                    EkipmanId: ekipmanId,
                    DosyaAdi: f.DosyaAdi,
                    ContentType: f.ContentType,
                    FileData: f.FileData
                  })
                )
              );
            } catch (err) {
              fotoHata = true;
            }
          }
          notify(
            fotoHata ? 'error' : 'success',
            fotoHata
              ? 'Ekipman kaydedildi ancak bazı fotoğraflar yüklenemedi.'
              : res.data.ErrorDescription || 'İşlem başarılı.'
          );
          setDialogOpen(false);
          fetchAll();
        } else {
          notify(
            'error',
            (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
          );
        }
      })
      .catch(() => notify('error', 'Kayıt sırasında bir hata oluştu.'))
      .finally(() => setLoading(false));
  };

  const handleDelete = (level, id) => {
    const urlMap = {
      ust: `/DeleteEkipmanUstGrup/${id}`,
      grup: `/DeleteEkipmanGrup/${id}`,
      ekipman: `/DeleteEkipman/${id}`
    };

    askConfirm(
      'Pasifleştir',
      'Bu kaydı pasif hale getirmek istediğinize emin misiniz?',
      () => {
        setLoading(true);
        axios
          .get(urlMap[level])
          .then(res => {
            if (res.data && res.data.ErrorCode === '0') {
              notify(
                'success',
                res.data.ErrorDescription || 'Kayıt pasif hale getirildi.'
              );
              fetchAll();
            } else {
              notify(
                'error',
                (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
              );
            }
          })
          .catch(() => notify('error', 'İşlem sırasında bir hata oluştu.'))
          .finally(() => setLoading(false));
      }
    );
  };

  // Pasif kaydı tekrar aktifleştir
  const handleReactivate = (level, row) => {
    let url;
    let payload;
    if (level === 'ust') {
      url = '/SetEkipmanUstGrup';
      payload = { Id: row.Id, Aciklama: row.Aciklama, Active: true };
    } else if (level === 'grup') {
      url = '/SetEkipmanGrup';
      payload = {
        Id: row.Id,
        UstGrupId: row.UstGrupId,
        Aciklama: row.Aciklama,
        Active: true
      };
    } else {
      url = '/SetEkipman';
      payload = {
        Id: row.Id,
        UstGrupId: row.UstGrupId,
        GrupId: row.GrupId,
        Description: row.Description,
        StokNo: row.StokNo || '',
        Aciklama: row.Aciklama || '',
        Marka: row.Marka || '',
        Model: row.Model || '',
        KalibrasyonIhtiyaci: !!row.KalibrasyonIhtiyaci,
        KalibrasyonGun: row.KalibrasyonIhtiyaci ? row.KalibrasyonGun : null,
        Active: true,
        UzmanlikIds: uzmanlikForEkipman(row.Id).map(x => x.UzmanlikId)
      };
    }
    setLoading(true);
    axios
      .post(url, payload)
      .then(res => {
        if (res.data && res.data.ErrorCode === '0') {
          notify('success', 'Kayıt aktif hale getirildi.');
          fetchAll();
        } else {
          notify(
            'error',
            (res.data && res.data.ErrorDescription) || 'İşlem başarısız.'
          );
        }
      })
      .catch(() => notify('error', 'İşlem sırasında bir hata oluştu.'))
      .finally(() => setLoading(false));
  };

  // Dialog'da grup seçimi için ilgili üst gruba bağlı AKTİF gruplar
  const dialogGruplar = gruplar.filter(
    g => g.Active && String(g.UstGrupId) === String(form.UstGrupId)
  );

  const renderRowActions = (level, row, onAdd) => (
    <div className={classes.rowActions}>
      {onAdd && (
        <Tooltip title={level === 'ust' ? 'Grup Ekle' : 'Ekipman Ekle'}>
          <IconButton size="small" color="primary" onClick={onAdd}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Düzenle">
        <IconButton size="small" onClick={() => row.onEdit()}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.Active ? (
        <Tooltip title="Pasifleştir">
          <IconButton size="small" onClick={() => handleDelete(level, row.Id)}>
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Aktifleştir">
          <IconButton size="small" onClick={() => handleReactivate(level, row)}>
            <RestoreFromTrashIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  return (
    <Page className={classes.root} title="Ekipman Admin Yönetimi">
      <Backdrop open={loading} style={{ zIndex: 2000, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <AlertScreen
          variant={snackType}
          message={snackMessage}
          onClose={() => setSnackOpen(false)}
        />
      </Snackbar>

      <Card>
        <CardHeader
          avatar={<CategoryIcon color="primary" />}
          title="Ekipman Admin Yönetimi"
          subheader="Üst grup → Grup → Ekipman (stok no) ağaç yapısını yönetin"
        />
        <Divider />
        <CardContent>
          <div className={classes.toolbar}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={openAddUst}
            >
              Yeni Üst Grup
            </Button>
            <div className={classes.spacer} />
            <FormControlLabel
              control={
                <Switch
                  checked={showPassive}
                  onChange={e => setShowPassive(e.target.checked)}
                  color="primary"
                />
              }
              label="Pasifleri göster"
            />
          </div>

          <List disablePadding>
            {ustGruplar.filter(visible).length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16 }}
              >
                Kayıt bulunamadı.
              </Typography>
            )}

            {ustGruplar.filter(visible).map(ust => {
              const ustOpen = !!expandedUst[ust.Id];
              const childGruplar = gruplar
                .filter(g => g.UstGrupId === ust.Id)
                .filter(visible);

              return (
                <React.Fragment key={`ust-${ust.Id}`}>
                  <ListItem
                    button
                    className={`${classes.ustRow} ${
                      ust.Active ? '' : classes.passive
                    }`}
                    onClick={() => toggleUst(ust.Id)}
                  >
                    {ustOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    <ListItemText
                      primary={<strong>{ust.Aciklama}</strong>}
                      secondary={!ust.Active ? 'Pasif' : null}
                    />
                    <div onClick={e => e.stopPropagation()}>
                      {renderRowActions(
                        'ust',
                        { ...ust, onEdit: () => openEditUst(ust) },
                        () => openAddGrup(ust.Id)
                      )}
                    </div>
                  </ListItem>

                  <Collapse in={ustOpen} timeout="auto" unmountOnExit>
                    {childGruplar.length === 0 && (
                      <Typography
                        variant="body2"
                        className={classes.emptyChild}
                      >
                        Bu üst gruba ait grup yok.
                      </Typography>
                    )}
                    {childGruplar.map(grup => {
                      const grupOpen = !!expandedGrup[grup.Id];
                      const childEkipmanlar = ekipmanlar
                        .filter(eq => eq.GrupId === grup.Id)
                        .filter(visible);

                      return (
                        <React.Fragment key={`grup-${grup.Id}`}>
                          <ListItem
                            button
                            className={`${classes.grupRow} ${
                              grup.Active ? '' : classes.passive
                            }`}
                            onClick={() => toggleGrup(grup.Id)}
                          >
                            {grupOpen ? (
                              <ExpandMoreIcon />
                            ) : (
                              <ChevronRightIcon />
                            )}
                            <ListItemText
                              primary={grup.Aciklama}
                              secondary={!grup.Active ? 'Pasif' : null}
                            />
                            <div onClick={e => e.stopPropagation()}>
                              {renderRowActions(
                                'grup',
                                { ...grup, onEdit: () => openEditGrup(grup) },
                                () => openAddEkipman(grup.UstGrupId, grup.Id)
                              )}
                            </div>
                          </ListItem>

                          <Collapse in={grupOpen} timeout="auto" unmountOnExit>
                            {childEkipmanlar.length === 0 && (
                              <Typography
                                variant="body2"
                                className={classes.emptyChild}
                              >
                                Bu gruba ait ekipman yok.
                              </Typography>
                            )}
                            {childEkipmanlar.map(eq => (
                              <ListItem
                                key={`ekipman-${eq.Id}`}
                                className={`${classes.ekipmanRow} ${
                                  eq.Active ? '' : classes.passive
                                }`}
                              >
                                <ListItemText
                                  primary={
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <FotoThumb
                                        ekipmanId={eq.Id}
                                        onClick={u =>
                                          setBigFoto({ open: true, url: u })
                                        }
                                      />
                                      <strong>{eq.Description}</strong>
                                    </span>
                                  }
                                  secondaryTypographyProps={{
                                    component: 'div'
                                  }}
                                  secondary={
                                    <div className={classes.detayWrap}>
                                      {!eq.Active ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Durum:
                                          </span>
                                          <span
                                            className={classes.fieldValue}
                                            style={{ color: '#c62828' }}
                                          >
                                            Pasif
                                          </span>
                                        </span>
                                      ) : null}
                                      {eq.Marka ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Marka:
                                          </span>
                                          <span className={classes.fieldValue}>
                                            {eq.Marka}
                                          </span>
                                        </span>
                                      ) : null}
                                      {eq.Model ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Model:
                                          </span>
                                          <span className={classes.fieldValue}>
                                            {eq.Model}
                                          </span>
                                        </span>
                                      ) : null}
                                      {eq.StokNo ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Stok Kodu:
                                          </span>
                                          <span className={classes.fieldValue}>
                                            {eq.StokNo}
                                          </span>
                                        </span>
                                      ) : null}
                                      {eq.Aciklama ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Açıklama:
                                          </span>
                                          <span
                                            className={classes.fieldValue}
                                            title={eq.Aciklama}
                                          >
                                            {eq.Aciklama}
                                          </span>
                                        </span>
                                      ) : null}
                                      {eq.KalibrasyonIhtiyaci ? (
                                        <span className={classes.fieldPill}>
                                          <span className={classes.fieldLabel}>
                                            Kalibrasyon:
                                          </span>
                                          <span className={classes.fieldValue}>
                                            {`Gerekli${
                                              eq.KalibrasyonGun != null
                                                ? ` • ${eq.KalibrasyonGun} gün`
                                                : ''
                                            }`}
                                          </span>
                                        </span>
                                      ) : null}
                                      {uzmanlikForEkipman(eq.Id).length > 0 && (
                                        <span className={classes.uzmanlikGroup}>
                                          <span
                                            className={classes.uzmanlikLabel}
                                          >
                                            Uzmanlıklar:
                                          </span>
                                          {uzmanlikForEkipman(eq.Id).map(x => (
                                            <Chip
                                              key={x.UzmanlikId}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                              className={classes.uzmanlikChip}
                                              label={x.Aciklama || x.UzmanlikId}
                                            />
                                          ))}
                                        </span>
                                      )}
                                    </div>
                                  }
                                />
                                {renderRowActions('ekipman', {
                                  ...eq,
                                  onEdit: () => openEditEkipman(eq)
                                })}
                              </ListItem>
                            ))}
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Ekle / Düzenle Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {dialogLevel === 'grup' && (
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Üst Grup</InputLabel>
                  <Select
                    label="Üst Grup"
                    value={form.UstGrupId}
                    onChange={e => setFormField('UstGrupId', e.target.value)}
                  >
                    {ustGruplar
                      .filter(u => u.Active)
                      .map(u => (
                        <MenuItem key={u.Id} value={u.Id}>
                          {u.Aciklama}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {dialogLevel === 'ekipman' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Üst Grup</InputLabel>
                    <Select
                      label="Üst Grup"
                      value={form.UstGrupId}
                      onChange={e => handleUstChangeInDialog(e.target.value)}
                    >
                      {ustGruplar
                        .filter(u => u.Active)
                        .map(u => (
                          <MenuItem key={u.Id} value={u.Id}>
                            {u.Aciklama}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={!form.UstGrupId}
                  >
                    <InputLabel>Grup</InputLabel>
                    <Select
                      label="Grup"
                      value={form.GrupId}
                      onChange={e => setFormField('GrupId', e.target.value)}
                    >
                      {dialogGruplar.map(g => (
                        <MenuItem key={g.Id} value={g.Id}>
                          {g.Aciklama}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={dialogLevel === 'ekipman' ? 'Donanım Adı' : 'Açıklama'}
                value={
                  dialogLevel === 'ekipman' ? form.Description : form.Aciklama
                }
                inputProps={{
                  maxLength: dialogLevel === 'ekipman' ? 100 : 400
                }}
                onChange={e =>
                  setFormField(
                    dialogLevel === 'ekipman' ? 'Description' : 'Aciklama',
                    e.target.value
                  )
                }
              />
            </Grid>

            {dialogLevel === 'ekipman' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Marka"
                    value={form.Marka}
                    inputProps={{ maxLength: 200 }}
                    onChange={e => setFormField('Marka', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Model"
                    value={form.Model}
                    inputProps={{ maxLength: 200 }}
                    onChange={e => setFormField('Model', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Stok Kodu"
                    value={form.StokNo}
                    inputProps={{ maxLength: 60 }}
                    onChange={e => setFormField('StokNo', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Açıklama"
                    value={form.Aciklama}
                    inputProps={{ maxLength: 500 }}
                    onChange={e => setFormField('Aciklama', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={uzmanliklar}
                    value={form.Uzmanliklar}
                    PopperComponent={AsagiPopper}
                    getOptionLabel={o => o.Aciklama || ''}
                    getOptionSelected={(o, v) => o.Kod === v.Kod}
                    onChange={(e, val) => setFormField('Uzmanliklar', val)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Uzmanlık (opsiyonel)"
                        placeholder="Uzmanlık ara / seç"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.KalibrasyonIhtiyaci}
                        color="primary"
                        onChange={e =>
                          setFormField('KalibrasyonIhtiyaci', e.target.checked)
                        }
                      />
                    }
                    label="Kalibrasyon İhtiyacı"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.Active}
                        color="primary"
                        onChange={e => setFormField('Active', e.target.checked)}
                      />
                    }
                    label="Aktif"
                  />
                </Grid>

                {form.KalibrasyonIhtiyaci && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="number"
                      label="Kalibrasyon Süresi (Gün) (opsiyonel)"
                      placeholder="örn. 365"
                      value={form.KalibrasyonGun}
                      inputProps={{ min: 0 }}
                      onChange={e =>
                        setFormField(
                          'KalibrasyonGun',
                          e.target.value.replace(/[^0-9]/g, '')
                        )
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fotoğraf (JPG / PNG — tek)
                  </Typography>
                  <div className={classes.fotoBox}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<AddIcon />}
                    >
                      {yeniFotolar.length > 0 || mevcutFotolar.length > 0
                        ? 'Fotoğrafı Değiştir'
                        : 'Fotoğraf Seç'}
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleFotoSec}
                      />
                    </Button>
                    <div className={classes.fotoWrap}>
                      {yeniFotolar.length > 0 ? (
                        <div className={classes.fotoThumb}>
                          <img
                            alt={yeniFotolar[0].DosyaAdi}
                            src={yeniFotolar[0].dataUrl}
                            className={classes.fotoImg}
                          />
                          <IconButton
                            size="small"
                            className={classes.fotoDel}
                            onClick={removeYeniFoto}
                          >
                            <DeleteIcon
                              style={{ fontSize: 16, color: '#fff' }}
                            />
                          </IconButton>
                          <span
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              fontSize: 9,
                              textAlign: 'center'
                            }}
                          >
                            yeni
                          </span>
                        </div>
                      ) : mevcutFotolar.length > 0 ? (
                        <div className={classes.fotoThumb}>
                          {mevcutFotolar[0].dataUrl ? (
                            <img
                              alt={mevcutFotolar[0].DosyaAdi}
                              src={mevcutFotolar[0].dataUrl}
                              className={classes.fotoImg}
                            />
                          ) : (
                            <span
                              style={{
                                fontSize: 10,
                                padding: 4,
                                display: 'block'
                              }}
                            >
                              {mevcutFotolar[0].DosyaAdi}
                            </span>
                          )}
                          <IconButton
                            size="small"
                            className={classes.fotoDel}
                            onClick={() =>
                              handleDeleteFoto(mevcutFotolar[0].Id)
                            }
                          >
                            <DeleteIcon
                              style={{ fontSize: 16, color: '#fff' }}
                            />
                          </IconButton>
                        </div>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Fotoğraf yok
                        </Typography>
                      )}
                    </div>
                  </div>
                </Grid>
              </>
            )}

            {dialogLevel !== 'ekipman' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.Active}
                      onChange={e => setFormField('Active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Aktif"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>İptal</Button>
          <Button variant="contained" color="secondary" onClick={handleSave}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onay Dialog (silme / pasifleştirme) */}
      <Dialog
        open={confirmState.open}
        onClose={closeConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmState.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmState.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Hayır</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const fn = confirmState.onConfirm;
              closeConfirm();
              if (fn) fn();
            }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fotoğraf büyütme */}
      <Dialog
        open={bigFoto.open}
        onClose={() => setBigFoto({ open: false, url: null })}
        maxWidth="md"
      >
        <DialogContent style={{ padding: 8 }}>
          {bigFoto.url && (
            <img
              src={bigFoto.url}
              alt="Ekipman fotoğrafı"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '80vh',
                margin: '0 auto'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBigFoto({ open: false, url: null })}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default EkipmanAdminYonetimi;
